import {
	forwardRef,
	type MouseEventHandler,
	type ReactNode,
	type Ref,
} from "react";
import { NavLink, type To } from "react-router";
import Icon from "../Icon";
import Spinner from "../Spinner";

type ButtonProps = {
	children?: ReactNode;
	to?: To | null;
	onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
	type?: "button" | "submit" | "reset";
	variant?: string;
	loading?: boolean;
	outline?: boolean;
	link?: boolean;
	icon?: string | null;
	iconSize?: "xs" | "sm" | "md" | "lg" | "xl";
	iconRight?: boolean;
	size?: "" | "xs" | "sm" | "md" | "lg" | "xl";
	className?: string;
	disabled?: boolean;
	download?: boolean;
} & Record<string, unknown>;

const isExternalTo = (to: To): boolean => {
	if (typeof to !== "string") {
		return false;
	}

	return (
		to.startsWith("http://") ||
		to.startsWith("https://") ||
		/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(to) // other protocols like mailto:, tel:, etc.
	);
};

const Button = forwardRef<HTMLElement, ButtonProps>(function Button(
	{
		children,
		to = null,
		onClick = undefined,
		type = "button",
		variant = "primary",
		loading = false,
		outline = false,
		link = false,
		icon = null,
		iconSize = undefined,
		iconRight = true,
		size = "",
		className = "",
		disabled = false,
		download = false,
		...props
	}: ButtonProps,
	ref
) {
	const isDisabled = disabled || loading;
	const btnClassName = `btn ${variant} ${size} ${
		loading ? "is-loading" : ""
	} ${outline ? "outline" : ""} ${link ? "link" : ""} ${
		isDisabled ? "disabled" : ""
	} ${className}`;

	const handleClick = (e: any) => {
		if (isDisabled) {
			e.preventDefault();
			e.stopPropagation();
			return;
		}
		onClick?.(e);
	};

	if (to) {
		const linkLikeProps = {
			"aria-disabled": isDisabled || undefined,
			tabIndex: isDisabled ? -1 : undefined,
		};
		const isExternal = isExternalTo(to);
		const isWebUrl =
			typeof to === "string" &&
			(to.startsWith("http://") || to.startsWith("https://"));

		if (isExternal || download) {
			return (
				<a
					href={to as string}
					className={btnClassName}
					onClick={handleClick}
					ref={ref as Ref<HTMLAnchorElement>}
					target={isWebUrl ? "_blank" : undefined}
					rel={isWebUrl ? "noopener noreferrer" : undefined}
					download={download}
					{...linkLikeProps}
					{...(props as object)}
				>
					{icon && !iconRight && (
						<Icon name={icon} size={iconSize || size || "md"} />
					)}
					{children}
					{icon && iconRight && (
						<Icon name={icon} size={iconSize || size || "md"} />
					)}
				</a>
			);
		}
		return (
			<NavLink
				to={to}
				className={btnClassName}
				onClick={handleClick}
				ref={ref as Ref<HTMLAnchorElement>}
				{...linkLikeProps}
				{...(props as object)}
			>
				{icon && !iconRight && <Icon name={icon} size={iconSize || size || "md"} />}
				{children}
				{icon && iconRight && <Icon name={icon} size={iconSize || size || "md"} />}
			</NavLink>
		);
	}

	return (
		<button
			className={btnClassName}
			onClick={
				handleClick as MouseEventHandler<HTMLButtonElement> | undefined
			}
			type={type}
			ref={ref as Ref<HTMLButtonElement>}
			disabled={isDisabled}
			{...(props as object)}
		>
			{loading && !iconRight && <Spinner inline size="xs" padding="" />}
			{!loading && icon && !iconRight && (
				<Icon name={icon} size={iconSize || size || "md"} />
			)}
			{children}
			{!loading && icon && iconRight && (
				<Icon name={icon} size={iconSize || size || "md"} />
			)}
			{loading && iconRight && <Spinner inline size="xs" padding="" />}
		</button>
	);
});

export default Button;
